/**
 * «Вышло обновление» — полоса внизу экрана.
 *
 * Программа открыта неделями: владелец не закрывает вкладку, техники работают
 * в установленном приложении. Выкаты при этом идут по нескольку раз в день, и
 * запущенная страница остаётся на ПРЕЖНЕМ коде, пока её не перезагрузят.
 *
 * 19.09.2026 это обошлось так: правило записи телефона уже жило на сервере,
 * владелец вписал номер — и тот остался без дефисов, потому что в его открытой
 * вкладке правила ещё не было. Со стороны выглядит как «сделали и не работает».
 *
 * Как узнаём об обновлении. Сборка кладёт в страницу имя файла с отпечатком
 * (`assets/index-hnkB3edl.js`); после выката имя другое. Раз в десять минут и
 * при возвращении на вкладку страница заново читает `index.html` мимо кэша и
 * сравнивает имя с тем, с которым запустилась. Отличается — значит, на сервере
 * новая сборка. Никакого отдельного файла версий заводить не нужно: он мог бы
 * разойтись с тем, что на самом деле отдаётся.
 *
 * Перезагрузку НЕ ДЕЛАЕМ САМИ. На экране может быть несохранённое, а
 * перезагрузка его теряет: черновик живёт в памяти вкладки. Поэтому полоса
 * предупреждает и предлагает, а при несохранённых правках прямо говорит, что
 * сначала надо сохранить.
 */

import { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { anyModuleDirty } from '../store/drafts.js';

/** Имя файла сборки, с которой запущена эта страница. */
function нынешняяСборка(): string | null {
  const скрипт = document.querySelector<HTMLScriptElement>('script[src*="/assets/index-"]');
  const src = скрипт?.getAttribute('src') ?? '';
  return /assets\/index-[^"']+\.js/.exec(src)?.[0] ?? null;
}

/** Имя файла сборки, которая лежит на сервере сейчас. */
async function сборкаНаСервере(): Promise<string | null> {
  try {
    const ответ = await fetch('/index.html', { cache: 'no-store' });
    if (!ответ.ok) return null;
    const html = await ответ.text();
    return /assets\/index-[^"']+\.js/.exec(html)?.[0] ?? null;
  } catch {
    // Нет связи — молчим: обновление подождёт, а сообщать тут не о чем.
    return null;
  }
}

/** Как часто спрашивать сервер, не вышло ли новое. */
const ПРОВЕРЯТЬ_РАЗ_В = 10 * 60 * 1000;

export function UpdateNotice() {
  const [вышло, setВышло] = useState(false);
  const [скрыто, setСкрыто] = useState(false);

  useEffect(() => {
    const своя = нынешняяСборка();
    // В разработке имени с отпечатком нет — и проверять нечего.
    if (!своя) return;

    let живо = true;
    const проверить = async () => {
      const серверная = await сборкаНаСервере();
      if (живо && серверная && серверная !== своя) setВышло(true);
    };

    const таймер = window.setInterval(проверить, ПРОВЕРЯТЬ_РАЗ_В);
    /* Вернулись на вкладку — самый вероятный момент, когда выкат уже прошёл. */
    const наВиду = () => {
      if (document.visibilityState === 'visible') void проверить();
    };
    document.addEventListener('visibilitychange', наВиду);
    void проверить();

    return () => {
      живо = false;
      window.clearInterval(таймер);
      document.removeEventListener('visibilitychange', наВиду);
    };
  }, []);

  if (!вышло || скрыто) return null;

  const естьНесохранённое = anyModuleDirty();

  return (
    <div
      className="fixed inset-x-2 bottom-2 z-50 mx-auto flex max-w-xl flex-wrap items-center gap-2 rounded-xl border px-3 py-2.5 shadow-lg md:inset-x-auto md:right-4 md:bottom-4"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      <RefreshCw size={16} className="flex-shrink-0 text-ink2" />
      <span className="min-w-0 flex-1 text-sm text-ink">
        Вышло обновление программы.{' '}
        {естьНесохранённое ? (
          <span style={{ color: 'var(--otc-amber)' }}>
            Сначала сохраните правки — при обновлении несохранённое пропадёт.
          </span>
        ) : (
          <span className="text-ink2">Обновите страницу, чтобы работать в новой версии.</span>
        )}
      </span>

      <button
        onClick={() => window.location.reload()}
        className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-transparent px-3 py-1.5 text-sm font-medium text-white"
        style={{ background: 'var(--otc-accent)' }}
      >
        Обновить
      </button>
      <button
        onClick={() => setСкрыто(true)}
        className="rounded-lg p-1.5 text-ink2 hover:bg-black/5"
        title="Скрыть до следующей проверки"
        aria-label="Скрыть"
      >
        <X size={16} />
      </button>
    </div>
  );
}
